import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { buildSheetModel } from "../parsers/sheetModelBuilder";
import { buildWorkbookModelsWithExcelJS } from "../parsers/excelJSParser";

/**
 * useWorkbookLoader
 * Receives the Excel binary payload (base64) from the parent (DocumentRPD),
 * decodes it, and runs it through both ExcelJS (primary) and SheetJS
 * (fallback / print source).
 *
 * IMPORTANT: This hook no longer makes its own /images/ API call.  The
 * payload is supplied by the parent via the `excelPayload` prop — which
 * comes from the single /images/ fetch issued by DocumentRPD.handleImageData
 * — to avoid the duplicate request that previously happened when
 * navigating multi-file uploads.
 *
 * Returns: { loading, error, sheetNames, sheetDataByName, setSheetDataByName,
 *            workbookBufferRef, rawWorkbookRef }
 */
export default function useWorkbookLoader({ excelPayload, onLocked }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sheetNames, setSheetNames] = useState([]);
  const [sheetDataByName, setSheetDataByName] = useState({});

  // Raw binary + SheetJS workbook are kept in refs so the print API can
  // re-parse them without forcing a re-render.
  const workbookBufferRef = useRef(null);
  const rawWorkbookRef = useRef(null);

  // Keep the latest onLocked callback in a ref so the effect doesn't re-run
  // (and re-process the workbook) whenever the parent re-renders and
  // passes a new inline function reference.
  const onLockedRef = useRef(onLocked);
  useEffect(() => { onLockedRef.current = onLocked; }, [onLocked]);

  useEffect(() => {
    let cancelled = false;

    const processPayload = async (payload) => {
      try {
        setLoading(true);
        setError("");

        const base64 = payload?.excelData;
        if (!base64) {
          // Parent supplied a payload that doesn't carry an Excel binary;
          // fall back to PDF rendering via the parent.
          if (!cancelled && typeof onLockedRef.current === "function") onLockedRef.current();
          return;
        }

        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const workbookBuffer = bytes.buffer;
        workbookBufferRef.current = workbookBuffer;

        let names = [];
        let dataMap = {};
        const fileType = String(payload?.fileType || "").toLowerCase();
        const lowerName = String(payload?.fileName || "").toLowerCase();
        const isLegacyXls = fileType === "xls" || (lowerName.includes(".xls") && !lowerName.includes(".xlsx"));

        if (!isLegacyXls) {
          try {
            const result = await buildWorkbookModelsWithExcelJS(workbookBuffer);
            names = result.sheetNames;
            dataMap = result.dataMap;
          } catch (e) {
            console.error("[useWorkbookLoader] ExcelJS parsing failed, falling back:", e);
            const errMsg = String(e?.message || e || "").toLowerCase();
            if (
              errMsg.includes("encrypt") || errMsg.includes("password") ||
              errMsg.includes("corrupted") || errMsg.includes("end of central directory")
            ) {
              if (!cancelled && typeof onLockedRef.current === "function") { onLockedRef.current(); return; }
            }
          }
        }

        if (names.length === 0) {
          let workbook;
          try {
            workbook = XLSX.read(workbookBuffer, { type: "array", cellFormula: true, cellStyles: true });
          } catch (xlsxErr) {
            const msg = String(xlsxErr?.message || xlsxErr || "").toLowerCase();
            if (msg.includes("encrypt") || msg.includes("password") || msg.includes("cfb") || msg.includes("corrupted")) {
              if (!cancelled && typeof onLockedRef.current === "function") { onLockedRef.current(); return; }
            }
            throw xlsxErr;
          }
          rawWorkbookRef.current = workbook;
          names = workbook.SheetNames || [];
          names.forEach((name) => {
            const ws = workbook.Sheets[name];
            dataMap[name] = buildSheetModel(ws || {});
          });
        } else {
          // ExcelJS handled rendering; still parse SheetJS for the print path.
          try {
            const wb = XLSX.read(workbookBuffer, { type: "array", cellFormula: true, cellStyles: true });
            rawWorkbookRef.current = wb;
          } catch (_) { /* non-fatal */ }
        }

        // Remember each sheet's rows AS LOADED. Edits replace `rows` with new
        // arrays (the model spread keeps `baseRows`), so live chart data can
        // tell which cells the reader actually changed.
        Object.keys(dataMap).forEach((n) => {
          if (dataMap[n] && dataMap[n].rows) dataMap[n].baseRows = dataMap[n].rows;
        });

        if (!cancelled) {
          setSheetNames(names);
          setSheetDataByName(dataMap);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load Excel.");
          setLoading(false);
        }
      }
    };

    if (excelPayload && excelPayload.excelData) {
      processPayload(excelPayload);
    } else {
      // No payload yet — DocumentRPD's /images/ fetch is still in flight.
      // Stay in the loading state; the effect will re-run once the prop
      // updates with the binary.
      setLoading(true);
    }

    return () => { cancelled = true; };
  }, [excelPayload]);

  return {
    loading, error, sheetNames, setSheetNames, sheetDataByName, setSheetDataByName,
    workbookBufferRef, rawWorkbookRef,
  };
}
